/**
 * Lightweight WebSocket chat client.
 * Connects to /api/ws/chat, auto-reconnects with exponential back-off, and
 * dispatches incoming events through a subscribe/notify list. The JWT is
 * passed as a `?token=...` query param so cross-origin custom domains work
 * even when the browser refuses to send cookies (Allow-Origin: * is
 * incompatible with credentials).
 */

import { getAuthToken } from "./api";

function wsUrl() {
  const token = getAuthToken();
  // Build the WebSocket URL from the current page origin so we stay
  // same-host regardless of deploy environment (cheatcraft.net, preview,
  // localhost…). On localhost dev the backend lives on :8001, so fall back
  // to REACT_APP_BACKEND_URL when the page is on :3000.
  let base;
  if (typeof window !== "undefined" && window.location) {
    if (window.location.port === "3000") {
      // Dev — use the configured backend URL (if any).
      const env = process.env.REACT_APP_BACKEND_URL;
      if (env) base = env.replace(/^http/, "ws");
    }
    if (!base) {
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      base = `${proto}//${window.location.host}`;
    }
  }
  if (!base) return null;
  return token ? `${base}/api/ws/chat?token=${encodeURIComponent(token)}` : `${base}/api/ws/chat`;
}

export class ChatSocket {
  constructor() {
    this.ws = null;
    this.handlers = new Set();
    this.queue = [];
    this.connected = false;
    this._reconnectAttempt = 0;
    this._closedByUser = false;
    this._heartbeat = null;
  }

  connect() {
    this._closedByUser = false;
    this._open();
  }

  _open() {
    const url = wsUrl();
    if (!url) return;
    try {
      this.ws = new WebSocket(url);
    } catch (_) {
      this._scheduleReconnect();
      return;
    }
    this.ws.onopen = () => {
      this.connected = true;
      this._reconnectAttempt = 0;
      // Drain queued sends.
      while (this.queue.length) this.ws.send(this.queue.shift());
      // Heartbeat to keep ingress proxies alive.
      this._heartbeat = setInterval(() => {
        if (this.ws && this.ws.readyState === 1) this.ws.send(JSON.stringify({ type: "ping" }));
      }, 25000);
    };
    this.ws.onmessage = (evt) => {
      let data;
      try { data = JSON.parse(evt.data); } catch (_) { return; }
      this.handlers.forEach((h) => { try { h(data); } catch (_) {} });
    };
    this.ws.onclose = () => {
      this.connected = false;
      if (this._heartbeat) clearInterval(this._heartbeat);
      this._heartbeat = null;
      if (!this._closedByUser) this._scheduleReconnect();
    };
    this.ws.onerror = () => {
      try { this.ws && this.ws.close(); } catch (_) {}
    };
  }

  _scheduleReconnect() {
    const delay = Math.min(15000, 800 * Math.pow(2, this._reconnectAttempt));
    this._reconnectAttempt += 1;
    setTimeout(() => { if (!this._closedByUser) this._open(); }, delay);
  }

  send(payload) {
    const data = JSON.stringify(payload);
    if (this.ws && this.ws.readyState === 1) {
      this.ws.send(data);
    } else {
      this.queue.push(data);
      if (!this.ws || this.ws.readyState >= 2) this._open();
    }
  }

  on(handler) {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  close() {
    this._closedByUser = true;
    if (this._heartbeat) clearInterval(this._heartbeat);
    try { this.ws && this.ws.close(); } catch (_) {}
    this.ws = null;
    this.connected = false;
  }
}

// Singleton — one socket per browser tab is enough.
export const chatSocket = new ChatSocket();
